// Domain types shared by the API, the SPA, and the CDK app.
//
// The Household / Membership block's first consumer is scripts/auth-bootstrap.ps1.
// The activity types' first consumer is the type-CRUD validation in Session 2.

export interface Household {
  householdId: string;
  name: string;
  ownerUserId: string;
  /** IANA zone, e.g. "America/Denver". Every calendar-day bucket is computed in it. */
  timezone: string;
  createdAt: string;
}

export type MemberRole = "owner" | "co_owner";

export interface Membership {
  householdId: string;
  userId: string;
  role: MemberRole;
  displayName: string;
  email: string;
  joinedAt: string;
}

/** Mirror of a Membership under the user partition (USER# / MEMBERSHIP#). */
export interface UserMembership {
  householdId: string;
  userId: string;
  role: MemberRole;
  joinedAt: string;
}

export interface UserProfile {
  userId: string;
  email: string;
  displayName: string;
  createdAt: string;
}

export interface Invite {
  householdId: string;
  email: string;
  invitedByUserId: string;
  createdAt: string;
}

export const FIELD_KINDS = ["number", "text", "enum", "boolean", "duration", "currency"] as const;
export type FieldKind = (typeof FIELD_KINDS)[number];

export interface ActivityField {
  /** Stable, immutable, kebab-case. Never renamed — logs key their values by this. */
  fieldId: string;
  label: string;            // 1-40 chars
  kind: FieldKind;
  /** Required for number/duration/currency. "min" | "hr" | "mi" | ISO currency code | free text. */
  unit?: string;
  /** enum only. 1-20 entries, each 1-40 chars. */
  options?: string[];
  required: boolean;
  order: number;            // ascending render order, 0-based
}

export type Visibility = "shared" | "private";

export interface ActivityType {
  householdId: string;
  activityTypeId: string;   // uuid
  name: string;             // 1-60 chars
  shortName: string;        // 1-14 chars, the button label
  iconName: string;         // lucide icon, kebab-case, e.g. "scissors"
  colorToken: ActivityColor;
  visibility: Visibility;
  /** Required iff visibility === "private". */
  ownerUserId?: string;
  fields: ActivityField[];  // max 12
  pinned: boolean;
  pinOrder?: number;
  /** Denormalized counters, atomic ADD on every log write. */
  useCount: number;
  lastLoggedAt?: string;
  archived: boolean;
  /** Seeded by auth-bootstrap.ps1; the standalone note logger addresses it by id. */
  builtIn?: boolean;
  createdAt: string;
  createdByUserId: string;
  updatedAt: string;
}

export type FieldValue = string | number | boolean;

export interface ActivityLog {
  householdId: string;
  logId: string;
  activityTypeId: string;
  occurredAt: string;       // ISO-8601 with offset
  /** Keyed by ActivityField.fieldId. Absent key === not recorded. */
  values: Record<string, FieldValue>;
  /** Universal optional note, independent of the type's field schema (D-1). */
  note?: string;
  photoKey?: string;        // S3 key under the media bucket
  loggedByUserId: string;
  performedByUserId?: string;
  source: "quick_button" | "chat" | "backfill" | "import";
  /** DENORMALIZED from the ActivityType at write time. REQUIRED so an omission
   *  is a typecheck error rather than a silent private-data leak (INV-1). */
  visibility: Visibility;
  ownerUserId?: string;
  createdAt: string;
  idempotencyKey?: string;
  deleted?: boolean;
  deletedAt?: string;
}

export const ACTIVITY_COLORS = [
  "#2563eb", "#db2777", "#059669", "#d97706", "#7c3aed", "#0891b2",
] as const;
export type ActivityColor = (typeof ACTIVITY_COLORS)[number];

export const THEMES = ["system", "light", "dark"] as const;
export type Theme = (typeof THEMES)[number];

export interface UserSettings {
  weeklyDigestEnabled: boolean;
  /** 0 = Sunday ... 6 = Saturday, matching Date#getDay. */
  weeklyDigestDay: number;
  overdueNudgeEnabled: boolean;
  overdueNudgeHour: number;
  emailAddress?: string;
  theme: Theme;
  defaultPerformedBySelf: boolean;
  /** When true, dashboard taps always open the log modal, even for types with no required fields. */
  oneTapOpensModal: boolean;
}

/** Server-side defaults applied to any absent settings key. */
export const DEFAULT_USER_SETTINGS: UserSettings = {
  weeklyDigestEnabled: true,
  weeklyDigestDay: 0,
  overdueNudgeEnabled: true,
  overdueNudgeHour: 8,
  theme: "system",
  defaultPerformedBySelf: true,
  oneTapOpensModal: false,
};

/** Seeded by auth-bootstrap.ps1; the note logger and LOG_NOTE tool address it by id. */
export const BUILT_IN_NOTE_TYPE_ID = "note";

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  seq: number;
  createdAt: string;
}

export type ProposedKind = "record_activity" | "define_activity_type" | "log_note";

export interface ProposedNewType {
  activityTypeId: string;
  name: string;
  shortName: string;
  iconName: string;
  colorToken: ActivityColor;
  visibility: Visibility;
  fields: ActivityField[];
}

export interface ProposedEvent {
  kind: ProposedKind;
  confirmation: string;
  occurredAt: string;
  activityTypeId?: string;
  values?: Record<string, FieldValue>;
  note?: string;
  performedByUserId?: string;
  newType?: ProposedNewType;
  /** Per-field errors from coerce/validate. Present ⇒ this row is not persistable (D-AI-1). */
  fieldErrors?: Record<string, string>;
}

export interface PendingProposal {
  pendingId: string;
  householdId: string;
  sessionId: string;
  seq: number;
  events: ProposedEvent[];
  confirmationText: string;
  createdAt: string;
  createdByUserId: string;
}

export type ChatTimeMode = "now" | "custom" | "proposed";

export interface SavedLogRef {
  logId: string;
  occurredAt: string;
  activityTypeId: string;
  kind: ProposedKind;
}

/** Metadata row for a saved analysis report. Markdown lives in S3 at `s3Key`. */
export interface AnalysisReport {
  householdId: string;
  reportId: string;
  title: string;
  rangeDays: number;
  activityTypeIds?: string[];
  createdAt: string;
  createdByUserId: string;
  s3Key: string;
  inputTokens: number;
  outputTokens: number;
  stopReason?: string;
}

/** Per-user, per-household overdue-nudge suppression timestamps, keyed by type id. */
export interface NudgeState {
  userId: string;
  householdId: string;
  lastNudgedAt: Record<string, string>;
}

/** ICS feed lookup row. The raw token is never stored; GSI2PK is ICS#<sha256>. */
export interface IcsToken {
  userId: string;
  householdId: string;
  createdAt: string;
}
